import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import Layout from './Layout';
import { CheckCircleIcon, XCircleIcon, EyeIcon } from '@heroicons/react/24/outline';

// Component for pending tracks list
const PendingTracksList = ({ tracks, onApprove, onReject }) => {
  const [orgNames, setOrgNames] = useState({});

  useEffect(() => {
    const fetchOrgNames = async () => {
      const names = {};
      await Promise.all(
        tracks
          .filter(track => track.organizationId)
          .map(async (track) => {
            try {
              const orgDoc = await getDoc(doc(db, 'organizations', track.organizationId));
              if (orgDoc.exists()) {
                names[track.organizationId] = orgDoc.data().name || 'Unknown Organization';
              }
            } catch (error) {
              names[track.organizationId] = 'Unknown Organization';
            }
          })
      );
      setOrgNames(names);
    };

    if (tracks.length > 0) {
      fetchOrgNames();
    }
  }, [tracks]);

  if (tracks.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <p className="text-gray-500">No tracks pending approval.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {tracks.map(track => (
        <div key={track.id} className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{track.name}</h3>
              <p className="text-gray-500 text-sm mb-2">{track.description}</p>
              <div className="flex gap-2 mb-2">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Pending Approval
                </span>
                {track.organizationId && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {orgNames[track.organizationId] || 'Loading...'}
                  </span>
                )}
                {track.visibility?.isPublic ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Public
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Organization Only
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Link
              to={`/content-tracks/${track.id}`}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <EyeIcon className="h-4 w-4 mr-2" />
              Review Track
            </Link>
            <button
              onClick={() => onApprove(track.id)}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
            >
              <CheckCircleIcon className="h-4 w-4 mr-2" />
              Approve
            </button>
            <button
              onClick={() => onReject(track.id)}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
            >
              <XCircleIcon className="h-4 w-4 mr-2" />
              Reject
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const ContentTracks = () => {
  const navigate = useNavigate();
  const { currentUser, isSuperAdmin } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [pendingTracks, setPendingTracks] = useState([]);
  const [archivedTracks, setArchivedTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'pending' | 'archived'

  useEffect(() => {
    const fetchTracks = async () => {
      try {
        setLoading(true);
        setError(null);

        const tracksRef = collection(db, 'contentTracks');
        
        // If super admin, fetch pending and archived tracks
        if (isSuperAdmin()) {
          const pendingQuery = query(
            tracksRef,
            where('approvalStatus', '==', 'pending')
          );
          const pendingSnap = await getDocs(pendingQuery);
          const pending = pendingSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setPendingTracks(pending);

          const archivedQuery = query(
            tracksRef,
            where('status', '==', 'archived')
          );
          const archivedSnap = await getDocs(archivedQuery);
          const archived = archivedSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setArchivedTracks(archived);
        }
        
        // Fetch public tracks (available to all users)
        const publicTracksQuery = query(
          tracksRef,
          where('visibility.isPublic', '==', true)
        );
        const publicTracksSnap = await getDocs(publicTracksQuery);
        let publicTracks = publicTracksSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // If user is logged in, also fetch their organization tracks
        if (currentUser) {
          const userOrgsRef = collection(db, 'users', currentUser.uid, 'organizations');
          const userOrgsSnap = await getDocs(userOrgsRef);
          const orgIds = userOrgsSnap.docs.map(doc => doc.id);

          let orgTracks = [];
          if (orgIds.length > 0) {
            const orgTracksQuery = query(
              tracksRef,
              where('organizationId', 'in', orgIds)
            );
            const orgTracksSnap = await getDocs(orgTracksQuery);
            orgTracks = orgTracksSnap.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
          }

          // Filter out pending tracks that aren't approved yet (unless super admin)
          const approvedTracks = [...orgTracks, ...publicTracks].filter(track => {
            if (track.status === 'archived') return false;
            if (isSuperAdmin()) return true; // Super admin sees all (excluding archived)
            return track.approvalStatus !== 'pending' && track.approvalStatus !== 'rejected';
          });
          
          // Combine and deduplicate tracks by ID
          const uniqueTracks = Array.from(
            new Map(approvedTracks.map(track => [track.id, track])).values()
          );
          setTracks(uniqueTracks);
        } else {
          // Not logged in - only show public approved tracks
          const approvedPublic = publicTracks.filter(track => 
            track.approvalStatus !== 'pending' &&
            track.approvalStatus !== 'rejected' &&
            track.status !== 'archived'
          );
          setTracks(approvedPublic);
        }
      } catch (err) {
        console.error('Error fetching tracks:', err);
        setError('Failed to load content tracks. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTracks();
  }, [currentUser, isSuperAdmin]);

  const handleApprove = async (trackId) => {
    try {
      await updateDoc(doc(db, 'contentTracks', trackId), {
        approvalStatus: 'approved',
        status: 'published',
        approvedBy: currentUser.uid,
        approvedAt: new Date()
      });
      
      setPendingTracks(prev => prev.filter(t => t.id !== trackId));
      window.location.reload();
    } catch (error) {
      console.error('Error approving track:', error);
      alert('Failed to approve track');
    }
  };

  const handleReject = async (trackId) => {
    const reason = window.prompt('Please provide a reason for rejection (optional):');
    try {
      await updateDoc(doc(db, 'contentTracks', trackId), {
        approvalStatus: 'rejected',
        rejectedBy: currentUser.uid,
        rejectedAt: new Date(),
        rejectionReason: reason || ''
      });
      
      // Update local state
      setPendingTracks(prev => prev.filter(t => t.id !== trackId));
    } catch (error) {
      console.error('Error rejecting track:', error);
      alert('Failed to reject track');
    }
  };

  const handleUnarchive = async (trackId) => {
    try {
      await updateDoc(doc(db, 'contentTracks', trackId), {
        status: 'draft',
        archivedAt: null,
        archivedBy: null
      });
      setArchivedTracks(prev => prev.filter(t => t.id !== trackId));
      // Refresh tracks list
      window.location.reload();
    } catch (error) {
      console.error('Error unarchiving track:', error);
      alert('Failed to unarchive track');
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Links */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Content Tracks</h1>
          <div className="flex space-x-4">
            {isSuperAdmin() && pendingTracks.length > 0 && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                {pendingTracks.length} Pending Approval
              </span>
            )}
            <Link
              to="/content-tracks/create?type=children"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#0076BE] hover:bg-blue-700"
            >
              Create Children's Track
            </Link>
            <Link
              to="/content-tracks/create"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#5ABA47] hover:bg-green-700"
            >
              Create New Track
            </Link>
          </div>
        </div>

        {/* Super Admin Tabs */}
        {isSuperAdmin() && (
          <div className="mb-6 border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('all')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'all'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                All Tracks
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'pending'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pending Approval
                {pendingTracks.length > 0 && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    {pendingTracks.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('archived')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'archived'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Archived
                {archivedTracks.length > 0 && (
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                    {archivedTracks.length}
                  </span>
                )}
              </button>
            </nav>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading content tracks...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 my-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Pending Approval Section (Super Admin Only) */}
        {!loading && !error && isSuperAdmin() && activeTab === 'pending' && (
          <PendingTracksList 
            tracks={pendingTracks} 
            onApprove={handleApprove}
            onReject={handleReject}
          />
        )}

        {/* All Tracks Section */}
        {!loading && !error && (activeTab === 'all' || !isSuperAdmin()) && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tracks.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500">No content tracks available.</p>
                <Link
                  to="/content-tracks/create"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#5ABA47] hover:bg-green-700 mt-4"
                >
                  Create Your First Track
                </Link>
              </div>
            ) : (
              tracks.map(track => (
                <div 
                  key={track.id} 
                  className="bg-white shadow rounded-lg p-6 hover:shadow-lg transition-shadow"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{track.name}</h3>
                  <p className="text-gray-500 text-sm mb-4">{track.description}</p>
                  <Link
                    to={`/content-tracks/${track.id}`}
                    className="text-[#0076BE] hover:text-blue-700 font-medium text-sm"
                  >
                    View Track →
                  </Link>
                </div>
              ))
            )}
          </div>
        )}

        {/* Archived Tracks Section (Super Admin Only) */}
        {!loading && !error && isSuperAdmin() && activeTab === 'archived' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {archivedTracks.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500">No archived tracks.</p>
              </div>
            ) : (
              archivedTracks.map(track => (
                <div 
                  key={track.id} 
                  className="bg-white shadow rounded-lg p-6 hover:shadow-lg transition-shadow border border-gray-200"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{track.name}</h3>
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full">Archived</span>
                  </div>
                  <p className="text-gray-500 text-sm mb-4">{track.description}</p>
                  <div className="flex items-center justify-between">
                    <Link
                      to={`/content-tracks/${track.id}`}
                      className="text-[#0076BE] hover:text-blue-700 font-medium text-sm"
                    >
                      View Track →
                    </Link>
                    <button
                      onClick={() => handleUnarchive(track.id)}
                      className="text-sm font-medium text-green-700 hover:text-green-800"
                    >
                      Unarchive
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ContentTracks; 